import { useState, type FormEvent } from 'react';
import {
  useTenantUsers, useGrantableRoles, useInviteTenantUser, useRemoveTenantUser,
} from '../../lib/api/hooks-tms.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { useSession } from '../../lib/auth/session.js';
import { ApiClientError } from '../../lib/api/client.js';
import { relativeTime } from '../../lib/format.js';

/**
 * Portal users for this account.
 *
 * A platform operator manages these for any customer; a tenant admin manages
 * them for their own PBX only. Same screen and same endpoints either way —
 * scope is enforced server-side, so there is no second implementation to keep
 * in step (ADR-019).
 */
export function UsersTab({ tenantId }: { tenantId: string }) {
  const can = useSession((s) => s.can);
  const users = useTenantUsers(tenantId);
  const remove = useRemoveTenantUser(tenantId);
  const me = useSession((s) => s.session?.user.id);
  const [inviting, setInviting] = useState(false);

  return (
    <>
      <div className="arc-row" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="arc-h2">Portal users</h2>
          <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            People who can sign in and manage this PBX.
          </p>
        </div>
        <div className="arc-spacer" />
        {can('user:invite') && (
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
            onClick={() => setInviting((v) => !v)}>
            <Icon name={inviting ? 'close' : 'plus'} size={13} />
            {inviting ? 'Cancel' : 'Invite user'}
          </button>
        )}
      </div>

      {users.error && <ErrorBanner error={users.error} />}
      {remove.error && <ErrorBanner error={remove.error} />}
      {inviting && <InviteForm tenantId={tenantId} onDone={() => setInviting(false)} />}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr><th>User</th><th>Role</th><th>Status</th><th>Last seen</th><th /></tr>
            </thead>
            {users.isLoading ? <TableSkeleton cols={5} /> : (
              <tbody>
                {users.data?.data.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="arc-stack">
                        <span style={{ fontWeight: 550 }}>{u.full_name ?? u.email}</span>
                        {u.full_name && <span className="arc-subtle">{u.email}</span>}
                      </div>
                    </td>
                    <td><Badge tone="info">{u.role_name}</Badge></td>
                    <td>
                      <Badge tone={u.status === 'active' ? 'ok' : 'muted'}>
                        <span className="arc-dot" aria-hidden="true" />{u.status}
                      </Badge>
                    </td>
                    <td className="arc-muted">
                      {u.last_seen_at ? relativeTime(u.last_seen_at) : 'never'}
                    </td>
                    <td className="arc-td-right">
                      {can('user:delete') && u.id !== me && (
                        <button
                          type="button" className="arc-btn arc-btn-ghost arc-btn-sm arc-row-actions"
                          onClick={() => {
                            if (confirm(`Remove ${u.email} from this account? They lose access immediately.`)) {
                              remove.mutate(u.id);
                            }
                          }}
                        >
                          Remove
                        </button>
                      )}
                      {u.id === me && <span className="arc-subtle">you</span>}
                    </td>
                  </tr>
                ))}

                {users.data?.pending.map((p) => (
                  <tr key={p.id} style={{ background: 'var(--arc-surface-2)' }}>
                    <td>
                      <div className="arc-stack">
                        <span>{p.email}</span>
                        <span className="arc-subtle">invited, not yet accepted</span>
                      </div>
                    </td>
                    <td><Badge tone="muted">{p.role_name}</Badge></td>
                    <td><Badge tone="warn"><span className="arc-dot" aria-hidden="true" />pending</Badge></td>
                    <td className="arc-muted">expires {relativeTime(p.expires_at).replace(' ago', '')}</td>
                    <td />
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {!users.isLoading && (users.data?.data.length ?? 0) === 0 &&
         (users.data?.pending.length ?? 0) === 0 && (
          <Empty icon="users" title="No portal users"
            hint="Invite someone to let them manage this PBX." />
        )}
      </div>
    </>
  );
}

function InviteForm({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const roles = useGrantableRoles(tenantId);
  const invite = useInviteTenantUser(tenantId);
  const [email, setEmail] = useState('');
  const [roleKey, setRoleKey] = useState('tenant_admin');
  const [link, setLink] = useState<string | null>(null);

  const err = invite.error instanceof ApiClientError ? invite.error : null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    invite.mutate({ email, role_key: roleKey }, {
      onSuccess: (r) => {
        // No mail is sent yet, so the link is surfaced here rather than
        // silently created and lost.
        setLink(`${window.location.origin}/invite/${r.token}`);
        setEmail('');
      },
    });
  };

  if (link) {
    return (
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <div className="arc-banner arc-banner-info" style={{ marginBottom: 10 }}>
          <Icon name="check" size={14} />
          <div>
            <div style={{ fontWeight: 600 }}>Invitation created</div>
            <div style={{ fontSize: 12 }}>
              Send this link. It works once, expires in 7 days, and only they set the password.
            </div>
          </div>
        </div>
        <div className="arc-row">
          <input className="arc-input arc-mono" readOnly value={link}
            onFocus={(e) => e.currentTarget.select()} aria-label="Invitation link" />
          <button type="button" className="arc-btn"
            onClick={() => { void navigator.clipboard?.writeText(link); }}>
            Copy
          </button>
          <button type="button" className="arc-btn" onClick={() => { setLink(null); onDone(); }}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }} onSubmit={submit}>
      {err && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <Icon name="warning" size={14} /><span>{err.message}</span>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr auto', gap: 12, alignItems: 'start' }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="iu-email">Email address</label>
          <input id="iu-email" type="email" className="arc-input" required value={email}
            onChange={(e) => setEmail(e.target.value)} placeholder="person@company.co.za" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="iu-role">Role</label>
          <select id="iu-role" className="arc-select" value={roleKey}
            onChange={(e) => setRoleKey(e.target.value)}>
            {roles.data?.data.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
          </select>
          {/* Only roles the inviter holds themselves are offered, and the API
              refuses the rest — the list is a convenience, not the control. */}
          <div className="arc-hint">
            {roles.data?.data.find((r) => r.key === roleKey)?.description}
          </div>
        </div>
        <div style={{ paddingTop: 23 }}>
          <button type="submit" className="arc-btn arc-btn-primary" disabled={invite.isPending}>
            {invite.isPending ? 'Creating…' : 'Create invitation'}
          </button>
        </div>
      </div>
    </form>
  );
}
